import { Browser } from '@capacitor/browser';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { hasFeatureEnabled } from '../../services/feature';
import {
  openPaywall,
  openWithInAppBrowser,
} from '../../services/in-app-browser';
import { useAppDispatch } from '../../services/store';
import {
  selectCurrentMember,
  selectHasCurrentMemberActiveSubscription,
} from '../../services/store/modules/backend-service';
import {
  fetchMemberSubscribed,
  showSubscriptionSuccess,
} from '../../services/store/modules/subscriptions';
import {
  fetchMemberSettings,
  getPaywallLastSeenDate,
  postMemberSettings,
} from '../../services/store/modules/app-controls';
import { parseConfigInt } from './parse-config-int';

const paywallThresholdLimitDay = parseConfigInt(
  window.DECKEE_CONFIG?.paywallThresholdLimitDay,
  30,
);

const paywallDelayAfterOnboardingInDays = parseConfigInt(
  window.DECKEE_CONFIG?.paywallDelayAfterOnboardingInDays,
  1,
);

interface UsePaywallParams {
  hasActiveSubscription: boolean;
  hasSubscriptionFeaturesEnabled: boolean;
  lastSeenPaywall: string | undefined;
  hasPaywallExceededThreshold: boolean;
  updateLastSeenPaywall: () => void;
  presentPaywall: () => void;
  presentSubscriptionPortal: (portalUrl: string) => void;
}

const usePaywall = (referrer: string): UsePaywallParams => {
  const dispatch = useAppDispatch();
  const { id, email, createdAt } = useSelector(selectCurrentMember) || {};

  const lastSeenPaywall = useSelector(getPaywallLastSeenDate);

  const [memberSettingsLoaded, setMemberSettingsLoaded] = useState(false);

  useEffect(() => {
    const runAsyncGetMemberSettings = async () => {
      // make sure member settings is fetched first
      await dispatch(fetchMemberSettings()).unwrap();
      setMemberSettingsLoaded(true);
    };

    if (id) {
      runAsyncGetMemberSettings();
    }
  }, [dispatch, id]);

  const browserListenerRef = useRef<any>();

  const hasActiveSubscription = useSelector(
    selectHasCurrentMemberActiveSubscription,
  );

  const hasSubscriptionFeaturesEnabled = hasFeatureEnabled(
    'subscription-features',
  );

  useEffect(() => () => {
    if (browserListenerRef.current) {
      browserListenerRef.current.remove();
    }
  });

  const shouldShowPaywall = () => {
    if (!memberSettingsLoaded || !id) {
      return false;
    }
    const currentDate = new Date();

    if (!lastSeenPaywall) {
      const userCreatedTime = new Date(createdAt);

      const createdTimeDifference =
        currentDate.getTime() - userCreatedTime.getTime();
      const createdDaysDifference = createdTimeDifference / (1000 * 3600 * 24);

      // If the paywall has never been seen, show it once the account has been active long enough
      return createdDaysDifference >= paywallDelayAfterOnboardingInDays;
    }

    const lastSeenPaywallDate = new Date(lastSeenPaywall);
    const timeDifference =
      currentDate.getTime() - lastSeenPaywallDate.getTime();
    const daysDifference = timeDifference / (1000 * 3600 * 24);

    return daysDifference >= paywallThresholdLimitDay;
  };
  const hasPaywallExceededThreshold = shouldShowPaywall();

  const updateLastSeenPaywall = useCallback(() => {
    dispatch(
      postMemberSettings({
        paywallConfig: {
          lastSeenDate: new Date().toISOString(),
        },
      }),
    );
  }, [dispatch]);

  const presentPaywall = useCallback(() => {
    const asyncShowPaywall = async (a: string, b: string, r: string) => {
      const openPaywallPromise = await openPaywall(a, b, r);
      const listener = await Browser.addListener(
        'browserFinished',
        async () => {
          const subscribed = await dispatch(fetchMemberSubscribed()).unwrap();

          if (subscribed) {
            dispatch(showSubscriptionSuccess());
            // Purchase with value is sent server-side via Meta CAPI (action_source=app)
            // from the Stripe checkout.session.completed webhook.
          }

          listener.remove();
        },
      );
      browserListenerRef.current = listener;
      return openPaywallPromise;
    };

    asyncShowPaywall(id, email, referrer);
  }, [dispatch, id, email, referrer]);

  const presentSubscriptionPortal = useCallback(
    (portalUrl: string) => {
      const asyncShowSubscriptionPortal = async (signedPortalUrl: string) => {
        const openPaywallPromise = await openWithInAppBrowser({
          url: signedPortalUrl,
          windowName: '_self',
        });
        const listener = await Browser.addListener(
          'browserFinished',
          async () => {
            await dispatch(fetchMemberSubscribed()).unwrap();
            // No action required for now.
            listener.remove();
          },
        );
        browserListenerRef.current = listener;
        return openPaywallPromise;
      };

      if (portalUrl !== '') {
        asyncShowSubscriptionPortal(portalUrl);
      }
    },
    [dispatch],
  );

  return {
    presentPaywall,
    hasPaywallExceededThreshold,
    updateLastSeenPaywall,
    hasSubscriptionFeaturesEnabled,
    hasActiveSubscription,
    lastSeenPaywall,
    presentSubscriptionPortal,
  };
};

export default usePaywall;
